import PauseCircleOutlineRounded from "@mui/icons-material/PauseCircleOutlineRounded";
import { Box, Typography } from "@mui/material";
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { InlineHint } from "../../../components/UIToolkit/molecules/InlineHint";
import { PanelHeader } from "../../../components/UIToolkit/molecules/PanelHeader";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { SystemDataGrid } from "../../../components/UIToolkit/Tables/SystemDataGrid";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment";
import { useLastNameFirst } from "../../../hooks/useNameFormat";
import { DUPLICATE_PAIR } from "../../../theme/tokens";
import type { SuspectRecord } from "../../../utils/nurseMergeModels";
import type { NurseResult } from "../hooks";
import { buildHeldRowColumns } from "./heldRowColumns";
import {
	addItemFor,
	addOutcomes,
	bulkAddSources,
	createOutcomes,
	type HeldOutcome,
	type HeldReviewRow,
	type HeldSource,
	heldReviewRows,
	heldSources,
	outcomeCounts,
} from "./heldRowModel";
import { useAddHeldToDepartment, useCreateHeldRows } from "./useHeldRowActions";

type Props = {
	results: NurseResult[];
	/** The mode the upload ran in (from its response) — a held row is created the way its upload would have created it. */
	isUpdate: boolean;
	dataTestId: string;
};

const rowId = (row: HeldReviewRow): string => row.key;

/**
 * Roster rows the upload held instead of creating: someone elsewhere in the organization already has a record with
 * the same name, and no email or phone told them apart. Per row the onboarder adds this facility to that record or
 * creates a separate record; a bulk action resolves every row with a single candidate. Renders nothing without held
 * rows. Outcomes are shown in place — the upload summary below still reports the rows as held.
 */
export const HeldRows: FC<Props> = ({ results, isUpdate, dataTestId }) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = chosenDepartment.department_id;
	const facility = chosenDepartment.display_name;
	const [outcomes, setOutcomes] = useState<ReadonlyMap<number, HeldOutcome>>(() => new Map());
	const sectionRef = useRef<HTMLDivElement>(null);
	const { mutate: addToDepartment, isPending: adding } = useAddHeldToDepartment();
	const { mutate: createHeld, isPending: creating } = useCreateHeldRows();
	const busy = adding || creating;

	const sources = useMemo(() => heldSources(results), [results]);
	const lastNameFirst = useLastNameFirst();
	const rows = useMemo(() => heldReviewRows(results, lastNameFirst), [results, lastNameFirst]);
	const bulkSources = bulkAddSources(sources, outcomes);
	const [warnings, setWarnings] = useState<string[]>([]);

	// The clicked button is replaced by its outcome, so focus would fall to the page body. Land it on this section.
	useEffect(() => {
		if (outcomes.size > 0) sectionRef.current?.focus();
	}, [outcomes]);

	const record = useCallback((next: Map<number, HeldOutcome>) => setOutcomes((current) => new Map([...current, ...next])), []);

	const add = useCallback(
		(picks: Array<{ source: HeldSource; candidate: SuspectRecord }>, bulk: boolean) => {
			const items = picks.map(({ source, candidate }) => addItemFor(source, candidate));
			addToDepartment(
				{ departmentId, items, bulk },
				{
					onSuccess: (response) =>
						record(
							addOutcomes(
								picks.map(({ source }) => source),
								response,
							),
						),
				},
			);
		},
		[addToDepartment, departmentId, record],
	);

	const create = useCallback(
		(source: HeldSource) =>
			createHeld(
				{ departmentId, isUpdate, rows: [source.row.held_row] },
				{
					onSuccess: (response) => {
						record(createOutcomes([source], response));
						setWarnings((current) => [...current, ...response.warnings]);
					},
				},
			),
		[createHeld, departmentId, isUpdate, record],
	);

	const columns = useMemo(
		() =>
			buildHeldRowColumns(t, {
				dataTestId,
				facility,
				outcomes,
				busy,
				onAdd: (row) => add([{ source: row.source, candidate: row.candidate }], false),
				onCreate: create,
			}),
		[t, dataTestId, facility, outcomes, busy, add, create],
	);

	if (rows.length === 0) return null;

	return (
		<Box
			ref={sectionRef}
			// Programmatic focus target only (see the effect above); it is never in the tab order.
			tabIndex={-1}
			sx={{ mb: 2, display: "flex", flexDirection: "column", gap: 1, outline: "none" }}
			data-testid={`${dataTestId}-held`}
		>
			<PanelHeader
				icon={<PauseCircleOutlineRounded />}
				title={t("onboarding_load.held.title", { count: sources.length })}
				endingContent={
					<SystemButton
						buttonVariant="secondary"
						size="small"
						onClick={() =>
							add(
								bulkSources.map((source) => ({ source, candidate: source.row.held_candidates[0] })),
								true,
							)
						}
						disabled={busy || bulkSources.length === 0}
						loading={adding}
						noFullWidthMobile
						data-testid={`${dataTestId}-held-add-all`}
					>
						{t("onboarding_load.held.add_all", { facility, count: bulkSources.length })}
					</SystemButton>
				}
				dataTestId={`${dataTestId}-held-header`}
			/>
			<InlineHint dataTestId={`${dataTestId}-held-hint`}>{t("onboarding_load.held.hint", { facility })}</InlineHint>
			<SystemDataGrid<HeldReviewRow>
				rows={rows}
				columns={columns}
				getRowId={rowId}
				containedHeight
				dataGridProps={{
					"aria-label": t("onboarding_load.held.grid_aria"),
					// Standard, not compact: the record cell is three lines tall, as on Team → Duplicate employees.
					density: "standard",
					rowHeight: DUPLICATE_PAIR.ROW_HEIGHT,
				}}
				dataTestId={`${dataTestId}-held-grid`}
			/>
			{warnings.length > 0 && (
				<InlineHint tone="warning" dataTestId={`${dataTestId}-held-warnings`}>
					{warnings.join(" ")}
				</InlineHint>
			)}
			{/* Always mounted so the first outcome is announced: a live region added with its text is often missed. */}
			<Typography role="status" aria-live="polite" variant="body2" color="text.secondary" data-testid={`${dataTestId}-held-outcome-line`}>
				{outcomes.size > 0 ? t("onboarding_load.held.outcome_line", outcomeCounts(outcomes)) : ""}
			</Typography>
		</Box>
	);
};
